"use client";

// Tiny utility that delays rendering its children until the browser is idle.
// We use this on the blog (and globally, by extension) to stop non-critical
// client components (leadgen popup runtime, toaster, analytics, INP monitor)
// from contributing to the initial JS evaluation window. That window is what
// Lighthouse measures as TBT (Total Blocking Time).
//
// Mounting strategy:
// 1. On the server (SSR), we render nothing -- these components are all
//    non-visual UI scaffolding, so omitting them from SSR is safe.
// 2. On the client, we wait for `requestIdleCallback`. Browsers fire this when
//    the main thread has spare time after the page is interactive.
// 3. Fallback for Safari (no requestIdleCallback): a 2000ms setTimeout, which
//    matches the deadline Lighthouse uses for "Time to Interactive".

import { useEffect, useState } from "react";

type IdleMountProps = {
  children: React.ReactNode;
  // Optional: cap how long we wait, even if the browser never goes idle.
  // Default 2000ms matches Lighthouse's TTI window.
  timeoutMs?: number;
};

export function IdleMount({ children, timeoutMs = 2000 }: IdleMountProps) {
  const [isIdle, setIsIdle] = useState(false);

  useEffect(() => {
    // SSR safety: bail if window isn't available (shouldn't happen in client
    // components, but defensive).
    if (typeof window === "undefined") return;

    let cancelled = false;
    let idleHandle: number | null = null;
    let timeoutHandle: number | null = null;

    const trigger = () => {
      if (cancelled) return;
      setIsIdle(true);
    };

    // requestIdleCallback isn't available in Safari (as of Safari 17), so we
    // always set a setTimeout fallback. Whichever fires first wins.
    if ("requestIdleCallback" in window) {
      // The `timeout` option ensures the callback runs by timeoutMs even if
      // the browser is busy.
      idleHandle = window.requestIdleCallback(trigger, { timeout: timeoutMs });
    }
    timeoutHandle = window.setTimeout(trigger, timeoutMs);

    return () => {
      cancelled = true;
      if (idleHandle !== null && "cancelIdleCallback" in window) {
        window.cancelIdleCallback(idleHandle);
      }
      if (timeoutHandle !== null) {
        window.clearTimeout(timeoutHandle);
      }
    };
  }, [timeoutMs]);

  if (!isIdle) return null;
  return <>{children}</>;
}
